import { Box, Button, Container, Tabs, VStack } from '@chakra-ui/react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useMemo } from 'react';
import { PageSurface } from '@/components/ui';
import TopNav, { type TabConfig } from './TopNav';

interface TopNavWithTabsProps {
  tabs: TabConfig[];
  defaultTab?: string;
  children: React.ReactNode;
  onBack?: () => void;
  backButtonLabel?: string;
  rightSection?: React.ReactNode | null;
  leftSection?: React.ReactNode;
  containerMaxW?: string;
  queryParamName?: string;
}

interface TabContentProps {
  value: string;
  children: React.ReactNode;
}

// TabContent is a marker component for identifying tab panels
export const TabContent: React.FC<TabContentProps> = ({ children }) => {
  return <>{children}</>;
};

TabContent.displayName = 'TabContent';

// Error boundary for tab content
class TabErrorBoundary extends React.Component<
  { children: React.ReactNode; tabValue: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; tabValue: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Error in tab ${this.props.tabValue}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Container maxW="md" py={8} textAlign="center">
          <VStack gap={4}>
            <div>Something went wrong loading this tab.</div>
            {/*
              A Chakra button rather than a styled `<button>`: theme colours are
              CSS variables the style prop cannot resolve, so the palette can
              only be reached through the component. Same retry affordance as
              the Flow editor's boundary.
            */}
            <Button variant="outline" size="sm" onClick={() => this.setState({ hasError: false })}>
              Try Again
            </Button>
          </VStack>
        </Container>
      );
    }

    return this.props.children;
  }
}

const TopNavWithTabs = React.memo<TopNavWithTabsProps>(
  ({
    tabs,
    defaultTab,
    children,
    onBack,
    backButtonLabel,
    rightSection,
    leftSection,
    containerMaxW = 'full',
    queryParamName = 'tab',
  }) => {
    // Calculate default value with validation - hooks must be called unconditionally
    const defaultValue = useMemo(() => {
      if (!tabs || tabs.length === 0) {
        return '';
      }
      if (defaultTab && tabs.some((tab) => tab.value === defaultTab)) {
        return defaultTab;
      }
      return tabs[0]?.value || '';
    }, [defaultTab, tabs]);

    const [activeTab, setActiveTab] = useQueryState(
      queryParamName,
      parseAsString.withDefault(defaultValue)
    );

    // Validate activeTab is in tabs array
    const validatedActiveTab = useMemo(() => {
      if (!tabs || tabs.length === 0) {
        return '';
      }
      if (tabs.some((tab) => tab.value === activeTab)) {
        return activeTab;
      }
      return defaultValue;
    }, [activeTab, tabs, defaultValue]);

    const handleTabChange = useCallback(
      (value: string) => {
        if (!tabs || tabs.length === 0) return;

        // Validate the new tab value before setting
        if (tabs.some((tab) => tab.value === value)) {
          setActiveTab(value);
        } else {
          console.warn(`Invalid tab value: ${value}`);
        }
      },
      [tabs, setActiveTab]
    );

    // Extract tab contents from children with better memoization
    const tabContents = useMemo(() => {
      const contents: Record<string, React.ReactNode> = {};

      // Only process children if they exist and tabs are valid
      if (!children || !tabs || tabs.length === 0) return contents;

      React.Children.forEach(children, (child) => {
        if (React.isValidElement<TabContentProps>(child) && child.type === TabContent) {
          const { value, children: tabChildren } = child.props;
          if (value && tabs.some((tab) => tab.value === value)) {
            contents[value] = tabChildren;
          } else if (value) {
            console.warn(`TabContent with value "${value}" does not match any tab`);
          }
        }
      });

      return contents;
    }, [children, tabs]); // More specific dependencies

    // Validate tabs prop after hooks
    if (!tabs || tabs.length === 0) {
      console.warn('TopNavWithTabs: tabs prop is empty or undefined');
      return null;
    }

    return (
      <PageSurface surfaceProps={{ display: 'flex', flexDirection: 'column' }}>
        <TopNav
          tabs={tabs}
          activeTab={validatedActiveTab}
          onTabChange={handleTabChange}
          onBack={onBack}
          backButtonLabel={backButtonLabel}
          rightSection={rightSection}
          leftSection={leftSection}
          borderBottom="1px solid"
          borderColor="border"
          flexShrink={0}
        />
        <Box flex={1} minH={0} overflow="auto">
          <Container maxW={containerMaxW} w="full" py={{ base: 5, xl: 6 }} px={{ base: 4, xl: 6 }}>
            <VStack gap={8} w="full" align="stretch">
              <Tabs.Root
                value={validatedActiveTab}
                onValueChange={(details) => handleTabChange(details.value)}
                lazyMount
                unmountOnExit
              >
                {tabs.map((tab) => (
                  <Tabs.Content
                    key={tab.value}
                    value={tab.value}
                    role="tabpanel"
                    aria-labelledby={`tab-${tab.value}`}
                    pt={0}
                  >
                    <TabErrorBoundary tabValue={tab.value}>
                      {tabContents[tab.value] || (
                        <div style={{ padding: '20px', color: '#666' }}>
                          No content available for this tab.
                        </div>
                      )}
                    </TabErrorBoundary>
                  </Tabs.Content>
                ))}
              </Tabs.Root>
            </VStack>
          </Container>
        </Box>
      </PageSurface>
    );
  }
);

TopNavWithTabs.displayName = 'TopNavWithTabs';

export default TopNavWithTabs;
